import { Button, DrawerBodyWrapper, DrawerFooterWrapper, Input, Select } from '@base'
import { useEffect, useMemo, useState } from 'react'
import { ADMIN_SOURCE, ADMIN_SUB_ROLES, CountryCodes, DRAWER_TYPES, INSPECTION_USER_DESIGNATIONS, MB_ROLES, ORG_USER_ROLES, REGION_TYPES, strings } from '@constants'
import { BiFieldWrapper, EditDetailWrapper, GridListWrapper } from '../styles'
import { useDispatch, useSelector } from 'react-redux'
import { isSuperAdmin } from 'src/utils/adminFilter'
import { Spacings } from '@styles'
import { toggleDrawer } from '@data/state/action/root'
import { getCountryDialCode } from '@utils'

const deriveStateFromProp = (user) => {
	const _state = {
		fullName: {
			key: 'full_name',
			value: user?.full_name || '',
			valid: true,
		},
		mobileNumber: {
			key: 'mobile_number',
			value: user?.mobile_number || '',
			valid: true,
			disabled: +user?.mobile_number !== 91,
		},
		emailId: {
			key: 'email_id',
			value: user?.email_id || '',
			valid: true,
		},
		departments: {
			key: 'departments',
			value: user?.departments || null,
			valid: true,
		},
		role: {
			key: 'role',
			value: user?.role || [],
			valid: true,
		},
		sub_role: {
			key: 'sub_role',
			value: user?.sub_role || 'PORTAL',
			valid: true,
		},
		country_code: {
			key: 'country_code',
			value: CountryCodes[0],
			valid: true,
		},
		oracle_id: {
			key: 'oracle_id',
			value: user?.oracle_id ?? '',
			valid: true,
		},
	}
	if (user) {
		_state.country_code.value = CountryCodes.find((item) => item.code === user?.country_code) || CountryCodes[0]
	}
	return _state
}
export const ADMIN_SOURCE_LIST = (ADMIN_SOURCE ?? []).map((item) => ({ key: item, label: item }))

const EditUserDetails = ({ user, update, refreshUpdate, isAdminUser, getAllAdmins, updateReportingManager, isEditAdmin = false }) => {
	const loggedInuser = useSelector((_state) => _state.getIn(['user', 'user']))
	const dispatch = useDispatch()
	const userData = useSelector((_state) => _state.getIn(['root', 'drawerTypeMeta', 'user']))

	const [details, setDetails] = useState(deriveStateFromProp())

	useEffect(() => {
		const dialCode = getCountryDialCode(user?.country_code)
		setDetails(deriveStateFromProp({ ...user, mobile_number: user?.mobile_number?.replace(dialCode, '')?.trim() }))
	}, [user])

	const updateState = (type, key) => (value) => {
		if (typeof value === 'string') {
			value = value.trimStart()
		}
		if (type === 'role') {
			value = value.role.trimStart()
		}
		if (type === 'platforms') {
			value = value.map((item) => item.key)
		}
		if (type === 'departments') {
			value = value.map((item) => item.key)
		}
		setDetails((details) => ({
			...details,
			[type]: {
				...details[type],
				[key]: value,
			},
		}))
	}

	const disableButton = useMemo(() => {
		return Object.values(details).some((d) => !d.valid)
	}, [details])

	const updateDetails = () => {
		let updatedObject = Object.values(details).reduce(
			(acc, value) => ({
				...acc,
				[value.key]: value.value,
			}),
			{}
		)
		updatedObject = {
			...updatedObject,
			mobile_number: getCountryDialCode(details.country_code.value.code) + details.mobileNumber.value,
			country_code: details?.country_code.value.code,
		}
		update(updatedObject, userData)
	}

	const deleteAdminHandler = () => {
		dispatch(toggleDrawer(true, DRAWER_TYPES.DELETE_ADMIN, { label: strings('delete', 'Admin'), user, getAllAdmins, refreshUpdate }))
	}

	let adminSubRoleTypes = Object.entries(INSPECTION_USER_DESIGNATIONS).map(([key, value]) => ({ key, sub_role: value }))
	let adminRoleTypes = Object.entries(ORG_USER_ROLES).map(([key, value]) => ({ key, role: value }))


	const renderAdminDetails = () => {
		let roles = Object.values(MB_ROLES)
			.map((i) => ({ role: i }))
			.sort((a, b) => {
				if (a.role < b.role) {
					return -1
				}
				if (a.role > b.role) {
					return 1
				}
				return 0
			})

		if (loggedInuser?.role === MB_ROLES.ADMIN) {
			roles = roles.filter((i) => i.role !== MB_ROLES.SUPER_ADMIN)
		}

		return (
			<GridListWrapper itemPerRow={[2, 2, 2]}>
				<Select
					id='select-role'
					label={strings('role')}
					placeholder={strings('select', 'role')}
					data={adminRoleTypes}
					displayKey='role'
					primaryKey='key'
					value={adminRoleTypes.find((item) => item.key === details?.role?.value) ?? {}}
					disabled={!isSuperAdmin(loggedInuser?.role)}
					onChange={updateState('role', 'value')}
				/>

				<Select
					label={strings('sub_role')}
					id='sub_role'
					data={adminSubRoleTypes}
					type='text'
					value={adminSubRoleTypes.find((item) => item.key === details?.sub_role?.value) ?? {}}
					onChange={(value) => updateState('sub_role', 'value')(value.key)}
					placeholder={strings(['select', 'sub_role'])}
					displayKey='sub_role'
					primaryKey='key'
					disabled={!isSuperAdmin(loggedInuser?.role)}
				/>
			</GridListWrapper>
		)
	}

	return (
		<>
			<DrawerBodyWrapper>
				<EditDetailWrapper>
					<Input
						id='user-name'
						type='text'
						placeholder={strings('your_full_name')}
						label={strings('name')}
						value={details?.fullName?.value}
						onChange={updateState('fullName', 'value')}
					/>
				</EditDetailWrapper>
				<EditDetailWrapper>
					<Input
						type='email'
						pattern='^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$'
						label={strings('email')}
						placeholder={strings('your_email_address')}
						setValidity={updateState('emailId', 'valid')}
						errorMap={{
							patternMismatch: `${strings('enter_valid_email')}`,
						}}
						value={details?.emailId?.value}
						onChange={updateState('emailId', 'value')}
					/>
				</EditDetailWrapper>
				{/* Mobile number (and its country code) are read-only while editing an existing user.
				    Editing them re-appended the dial code onto the number, which let duplicate users
				    with the same mobile be created. The number is set at creation only. */}
				<BiFieldWrapper>
					<Select
						id={'country-code'}
						data={CountryCodes}
						displayKey='dial_code'
						primaryKey='name'
						value={CountryCodes.find((item) => details?.country_code?.value?.code === item.code) ?? CountryCodes[0]}
						onChange={(val) => updateState('country_code', 'value')(val)}
						type='tel'
						noDropIcon
						disabled={!!details?.mobileNumber?.value}
					/>
					<Input
						id='user-mobile'
						label={strings('mobile')}
						type='tel'
						placeholder={strings('enter_your_mobile_num')}
						maxLength={12}
						minLength={!details?.mobileNumber?.disabled && 7}
						pattern='^\d{7}\d+$'
						value={details?.mobileNumber?.value}
						onChange={updateState('mobileNumber', 'value')}
						disabled={!!details?.mobileNumber?.value}
						errorMap={{
							patternMismatch: `${strings('enter_valid_num')}`,
							valueMissing: `${strings('mob_num_required')}`,
						}}
						setValidity={updateState('mobileNumber', 'valid')}
					/>
				</BiFieldWrapper>
				{renderAdminDetails()}
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<Button small uppercase type='primary' disabled={disableButton} onClick={updateDetails}>
					{strings('update')}
				</Button>
				{isAdminUser && (
					<Button
						small
						uppercase
						type='secondary'
						margin={`0 ${Spacings.SPACING_2B} 0 0`}
						variant='destructive'
						onClick={deleteAdminHandler}
					>
						{strings('delete', 'admin')}
					</Button>
				)}
			</DrawerFooterWrapper>
		</>
	)
}

export default EditUserDetails
